import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import useScreenSize from '../../../../hooks/useScreenSize';
import YoutubeVideoInPopup from '../../../02_molecules/YoutubeVideoInPopup';
import ActivismPlayIcon from '../../../../public/static/icons/activism-play.svg';
import Slider from '../../../01_atoms/Slider';
import SliderNavigationIcon from '../../../../public/static/icons/arrows/carousel-button-outline.svg';
import {
  behaviorSettingsProps,
  generateClassNameByBehaviorSettings,
} from '../../../../utils/behaviorSettings';
import { pushSliderHasChanged as gtmPushSliderHasChanged } from '../../../../utils/gtm';

import styles from './index.module.scss';

const VideoPreviewItem = ({ image, title, onClick, className = '' }) => (
  <div
    role="button"
    tabIndex="0"
    aria-haspopup="true"
    className={`${styles['bb-youtube-videos__item-content']} ${className ? className : ''}`}
    onClick={onClick}
    onKeyDown={(key) => {
      if (key.key === 'Enter' || key.key === 'Space' || key.key === ' ') {
        onClick();
      }
    }}
    style={{
      backgroundImage: `linear-gradient(27.82deg, rgba(0, 0, 0, 0.4) 23.46%, rgba(0, 0, 0, 0) 100%), url(${image})`,
    }}
  >
    <div className={styles['bb-youtube-videos__item-play-icon']}>
      <ActivismPlayIcon />
    </div>
    <h3 className={styles['bb-youtube-videos__item-title']}>{title}</h3>
  </div>
);

VideoPreviewItem.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  className: PropTypes.string,
};

const defaultSliderSettings = {
  dots: false,
  infinite: false,
  lazyLoad: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  initialSlide: 0,
  variableWidth: true,
};

const BBYoutubeVideos = ({
  heading = '',
  variant = 'slim',
  items,
  behaviorSettings = null,
  uuid = null,
}) => {
  const [openedVideoIndex, setOpenedVideoIndex] = useState(0);
  const [slideIndex, setSlideIndex] = useState(1);
  const { width } = useScreenSize();

  let countItems = items.length;

  // Currently we support Large variant in case when only 1 video added.
  if (countItems !== 1) {
    variant = 'slim';
  }

  const sliderRef = useRef(null);
  const onNextClick = () => sliderRef.current && sliderRef.current.slickNext();
  const onPrevClick = () => sliderRef.current && sliderRef.current.slickPrev();

  const onVideoPopupClose = () => {
    // Focus the body block when popup is closed.
    const element = document.getElementById(uuid);
    if (element) {
      const videos = element.querySelectorAll('*[role="button"]');
      if (videos.length >= openedVideoIndex) {
        videos[openedVideoIndex - 1].focus();
      }
    }
    setOpenedVideoIndex(0);
  };

  const sliderSettings = defaultSliderSettings;
  sliderSettings.afterChange = (index) => {
    const newIndex = Math.round(index + 1);

    setSlideIndex(newIndex);

    gtmPushSliderHasChanged({
      uuid,
      type: 'youtube_videos_carousel',
      current_item: newIndex,
    });
  };

  let slideWidth = 308;
  let displayArrows = true;

  if (items.length < 2) {
    const calculatedWidth = width > 576 ? 540 : width;
    displayArrows = false;
    slideWidth = calculatedWidth - 40;
  }

  if (items.length < 3 && width > 767) {
    displayArrows = false;
    slideWidth = 680 / items.length;
  }
  if (items.length < 4 && width > 991) {
    displayArrows = false;
    const calculatedWidth = width > 1100 ? 1100 : width;
    slideWidth = (calculatedWidth - 140 - 16 * (items.length - 1)) / items.length;
  }

  if (items.length < 5 && width >= 1200) {
    displayArrows = false;
    const calculatedWidth = width > 1420 ? 1420 : width;
    slideWidth = (calculatedWidth - 140 - 16 * (items.length - 1)) / items.length;
  }

  sliderSettings.draggable = displayArrows;
  sliderSettings.swipe = displayArrows;

  const classes = [
    'bb',
    styles['bb-youtube-videos'],
    generateClassNameByBehaviorSettings(behaviorSettings),
  ];

  return (
    <div id={uuid} className={classes.join(' ')}>
      <div className="container">
        {heading && <h2 className={styles['bb-youtube-videos__heading']}>{heading}</h2>}
      </div>

      <div
        className={`${styles['slider']} ${items.length === 1 ? styles[`slider__standalone`] : ''} `}
      >
        <Slider {...sliderSettings} ref={sliderRef}>
          {items.map((video, index) => (
            <div
              key={video.videoURL}
              className={`${styles['bb-youtube-videos__item']} ${
                styles['bb-youtube-videos__slide']
              } ${styles[`bb-youtube-videos__item--${countItems}`]}`}
              style={{ width: slideWidth }}
            >
              <VideoPreviewItem
                onClick={() => setOpenedVideoIndex(index + 1)}
                title={video.previewTitle}
                image={
                  variant === 'large' && items.length === 1
                    ? video.previewImageLarge
                    : video.previewImage
                }
                className={styles[`bb-youtube-videos__item-content--${variant}`]}
              />
            </div>
          ))}
        </Slider>
        {displayArrows && (
          <div className="container">
            <div className={styles['slider__arrows']}>
              <SliderNavigationIcon
                className={`${styles['slider__arrow']} ${styles['slider__arrow--prev']} ${
                  slideIndex === 1 ? styles['slider__arrow--inactive'] : ''
                }`}
                onClick={onPrevClick}
                onKeyPress={onPrevClick}
                tabIndex="0"
              />
              <div className={styles['slider__info']}>
                {slideIndex}/{countItems}
              </div>
              <SliderNavigationIcon
                className={`${styles['slider__arrow']} ${styles['slider__arrow--next']} ${
                  slideIndex === countItems ? styles['slider__arrow--inactive'] : ''
                }`}
                onClick={onNextClick}
                onKeyPress={onNextClick}
                tabIndex="0"
              />
            </div>
          </div>
        )}
      </div>

      {/* Add popups */}
      {!!openedVideoIndex && (
        <YoutubeVideoInPopup
          isVisible
          acceptCookiesButtonClassName="youtube-videos-popup"
          onPopupClose={onVideoPopupClose}
          videoProps={items[openedVideoIndex - 1]}
        />
      )}
    </div>
  );
};

BBYoutubeVideos.propTypes = {
  heading: PropTypes.string,
  variant: PropTypes.oneOf(['slim', 'large']),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      videoURL: PropTypes.string.isRequired,
      autoPlay: PropTypes.bool,
      previewImage: PropTypes.string.isRequired,
      previewImageLarge: PropTypes.string.isRequired,
      previewTitle: PropTypes.string,
    }),
  ).isRequired,
  behaviorSettings: behaviorSettingsProps,
  uuid: PropTypes.string.isRequired,
};

export default BBYoutubeVideos;
